<script setup>
  import { computed } from "vue";
  import { storeToRefs } from "pinia";
  import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
  import { Button } from "@/components/ui/button";
  import BudgetTableSubcampaignRow from "./BudgetTableSubcampaignRow.vue";
  import BudgetTableAdsetRow from "./BudgetTableAdsetRow.vue";
  import { useMCampaignStore } from "@stores/mCampaign";
  import { useCCampaignsStore } from "@stores/cCampaigns";
  import { useCAdsetsStore } from "@stores/cAdsets";
  import { useBudgetView } from "@composables/budget/useBudgetView";

  const { currentMCampaign } = storeToRefs(useMCampaignStore());

  const cCampaignsStore = useCCampaignsStore();
  const { cCampaigns } = storeToRefs(cCampaignsStore);
  const { toggleCCampaign } = cCampaignsStore;

  const cAdsetsStore = useCAdsetsStore();
  const { cAdsets, cCampaignStatusFilter } = storeToRefs(cAdsetsStore);
  const { isCAdsetVisible, clearFiltersCAdsets } = cAdsetsStore;

  const { showConstraints, isCollapsed, toggleCollapse } = useBudgetView();

  const visibleCCampaigns = computed(() =>
    cCampaigns.value.filter(
      (cc) => cCampaignStatusFilter.value.length === 0 || cCampaignStatusFilter.value.includes(cc.status),
    ),
  );

  const adsetsByCampaign = (groupToken) =>
    cAdsets.value.filter((c) => c.group_token === groupToken).filter((c) => isCAdsetVisible(c));

  const totalDailyBudget = (groupToken) =>
    cAdsets.value
      .filter((c) => c.group_token === groupToken && c.status === "active")
      .reduce((sum, c) => sum + Number(c.daily_budget || 0), 0);

  const handleToggleCCampaign = (groupToken) => toggleCCampaign(currentMCampaign.value.token, groupToken);

  const totalColumns = computed(() => (showConstraints.value ? 11 : 9));

  const isEmpty = computed(() =>
    visibleCCampaigns.value.every((cc) => adsetsByCampaign(cc.group_token).length === 0),
  );

  const resetFilters = () => clearFiltersCAdsets();
</script>

<template>
  <div class="min-h-0 flex-1 overflow-auto border-t">
    <Table class="table-fixed">
      <TableHeader class="sticky top-0 z-10 bg-background">
        <TableRow>
          <TableHead class="w-10"></TableHead>
          <TableHead class="w-14"></TableHead>
          <TableHead :class="['w-[260px]', showConstraints ? 'sticky left-[6rem] z-20 bg-background' : '']">
            Campaign / Ad set
          </TableHead>
          <TableHead class="w-28 text-right">Daily budget</TableHead>
          <TableHead class="w-24 text-right">Recos</TableHead>
          <TableHead class="w-24 text-right">Manual</TableHead>
          <TableHead
            v-if="showConstraints"
            class="w-20 text-right"
            >Min</TableHead
          >
          <TableHead
            v-if="showConstraints"
            class="w-20 text-right"
            >Max</TableHead
          >
          <TableHead class="w-16 text-right">pCPI</TableHead>
          <TableHead class="w-16 text-right">Alpha</TableHead>
          <TableHead class="w-16 text-center">pQuota</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        <template
          v-for="ccampaign in visibleCCampaigns"
          :key="ccampaign.group_token">
          <BudgetTableSubcampaignRow
            :ccampaign="ccampaign"
            :total-daily="totalDailyBudget(ccampaign.group_token)"
            :collapsed="isCollapsed(ccampaign.group_token)"
            @toggle-collapse="toggleCollapse(ccampaign.group_token)"
            @toggle-status="handleToggleCCampaign(ccampaign.group_token)" />
          <template v-if="!isCollapsed(ccampaign.group_token)">
            <BudgetTableAdsetRow
              v-for="cAdset in adsetsByCampaign(ccampaign.group_token)"
              :key="cAdset.id"
              :c-adset="cAdset"
              :ccampaign="ccampaign" />
          </template>
        </template>
        <TableRow v-if="isEmpty">
          <TableCell
            :colspan="totalColumns"
            class="py-10 text-center text-sm text-muted-foreground">
            <p>No adsets match the current filters.</p>
            <Button
              variant="link"
              size="sm"
              @click="resetFilters">
              Clear filters
            </Button>
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  </div>
</template>
